import { useContext } from 'react'
import { useQuery } from 'react-query'
import { request } from 'Features/auth/axios-client'
import {
  ScheduleVariantContext,
  type ScheduleVariantKey,
} from 'Features/SchedulerView/SchedulerView.data'
import type { UtilisationData } from './Utilisation.types'

const fetchUtilisation = async (
  scheduleId: string,
  iteration: string,
  week: number | undefined,
  scheduleVariant: ScheduleVariantKey
): Promise<UtilisationData | null> => {
  const params = new URLSearchParams()
  if (week !== undefined) params.set('week', String(week))
  // Omitted for the run's own answer, so that request is unchanged.
  if (scheduleVariant !== 'balanced')
    params.set('scheduleVariant', scheduleVariant)
  const qs = params.toString() ? `?${params.toString()}` : ''
  // request() returns an AxiosResponse; .data is the JSON body
  // ({ data: <utilisation>, status: "OK" }) — unwrap one more level.
  const response = await request({
    url: `/schedulerView/${scheduleId}/${iteration}/utilisation${qs}`,
    method: 'GET',
  })
  return (response?.data?.data as UtilisationData) ?? null
}

export const useUtilisationData = (
  scheduleId: string,
  iteration: string,
  week?: number
) => {
  // The answer picked in the schedule drop-down, like every other results
  // view. It was missing here, so the reports kept showing the run's own
  // answer whichever one was picked (client feedback 2026-10-06).
  const scheduleVariant = useContext(ScheduleVariantContext)
  return useQuery(
    ['scheduler-utilisation', scheduleId, iteration, week, scheduleVariant],
    () => fetchUtilisation(scheduleId, iteration, week, scheduleVariant),
    {
      enabled: !!scheduleId,
      staleTime: 60_000,
      keepPreviousData: true,
    }
  )
}

export const DAY_LABELS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const

export const formatNumber = (n: number, digits = 2): string =>
  Number.isFinite(n)
    ? n.toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    : '—'

export const formatPct = (n: number): string =>
  Number.isFinite(n) ? `${n.toFixed(0)}%` : '—'

export const formatInt = (n: number): string =>
  Number.isFinite(n) ? Math.round(n).toLocaleString() : '—'
